import { z } from 'zod';
import { getErrorMessage } from '@/utils';
import {
  emailSupportSchema,
  reportDisputeFormSchema,
} from '@/schemas/emailSupport';
import {
  sendReportDisputeEmail,
  sendSupportRequestEmail,
  sendWelcomeEmail,
  sendWelcomeMentorEmail,
} from './aws';
import type {
  SendWelcomeEmailType,
  SendWelcomeMentorEmailType,
} from '../email-templates/types';

async function createEmailAttachmentFromFile(
  file: File
): Promise<{ filename: string; content: string; contentType: string }> {
  const arrayBuffer = await file.arrayBuffer();
  const base64Content = Buffer.from(arrayBuffer).toString('base64');

  return {
    filename: file.name,
    content: base64Content,
    contentType: file.type,
  };
}

export async function submitEmailSupport(
  input: z.infer<typeof emailSupportSchema>
) {
  try {
    const data = emailSupportSchema.parse(input);

    let attachment = undefined;

    if (data.attachments && data.attachments[0]) {
      attachment = await createEmailAttachmentFromFile(data.attachments[0]);
    }

    const emailProps = {
      name: data.name,
      role: data.role,
      subject: data.subject,
      fromAddress: data.email,
      details: data.details,
      attachment: attachment,
    };

    await sendSupportRequestEmail(emailProps);

    return {
      success: {
        message: 'Your support request has been sent successfully!',
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
}

export async function submitEmailReportDispute(
  input: z.infer<typeof reportDisputeFormSchema>
) {
  try {
    const data = reportDisputeFormSchema.parse(input);

    let attachment = undefined;

    if (data.attachments && data.attachments[0]) {
      attachment = await createEmailAttachmentFromFile(data.attachments[0]);
    }

    const emailProps = {
      name: data.name,
      role: data.role,
      subject: data.subject,
      fromAddress: data.email,
      details: data.details,
      category: data.category,
      attachment: attachment,
    };

    await sendReportDisputeEmail(emailProps);

    return {
      success: {
        message: 'Your report has been sent successfully!',
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
}

export async function submitWelcomeEmail(input: SendWelcomeEmailType) {
  try {
    await sendWelcomeEmail(input);

    return {
      success: {
        message: 'Welcome email has been sent successfully!',
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
}

export async function submitWelcomeMentorEmail(
  input: SendWelcomeMentorEmailType
) {
  try {
    await sendWelcomeMentorEmail(input);

    return {
      success: {
        message: 'Welcome mentor email has been sent successfully!',
      },
    };
  } catch (error) {
    return {
      error: getErrorMessage(error),
    };
  }
}
